import React, {useState, useEffect} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  Alert,
} from '@mui/material'
import SearchableSelect from '../forms/SearchableSelect'
import axios from 'axios'
import {toast} from 'react-toastify'
import {DatePicker, LocalizationProvider, TimePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, {Dayjs} from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {useDispatch} from 'react-redux'
import {getProgram} from '../../pages/admin/care/enrollment/store/actions'
dayjs.extend(utc)
dayjs.extend(timezone)

interface NoteEntryModalProps {
  open: boolean
  onClose: () => void
  noteCheckingRoute: string // Route for checking if note/assessment exists
  redirectToNewPage: (data: any) => void
  isWoundType?: boolean
  isCaregiverType?: boolean
}

const NoteEntryModal: React.FC<NoteEntryModalProps> = ({
  open,
  onClose,
  noteCheckingRoute,
  redirectToNewPage,
  isWoundType = false,
  isCaregiverType = false,
}) => {
  const [selectedClient, setSelectedClient] = useState<{
    value: string | number
    label: string
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [noteType, setNoteType] = useState<{value: string; label: string} | null>(null)
  const [clients, setClients] = useState<{value: string; label: string}[]>([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [programsLoading, setProgramsLoading] = useState(false)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)
  const [date, setDate] = useState<Dayjs | null>(null)
  const [programs, setPrograms] = useState<any[]>([])
  const [selectedProgram, setSelectedProgram] = useState<{
    value: string | number
    label: string
  } | null>(null)
  const dispatch = useDispatch()

  const handleStartDateChange = (newStartDate: Dayjs | null) => {
    setDate(newStartDate)
  }
  useEffect(() => {
    if (open) {
      setSelectedClient(null)
      setNoteType(null)
      setAssessmentError(null)
      fetchClients()
    }
  }, [open])

  const fetchClients = async () => {
    try {
      setClientsLoading(true)
      
      if (isCaregiverType) {
        setProgramsLoading(true)
        const [clientData, programData]: any = await Promise.all([
          axios.get('/clients/getClientNames'),
          dispatch(getProgram({})),
        ])
        const programs = programData.payload.data
        const {data} = clientData?.data
        const updatedPrograms = programs.map((program: any) => ({
          value: program._id,
          label: program.programName,
        }))
        const updatedClients = data.map((client: any) => ({
          value: client._id,
          label: client.fullName,
        }))
        setPrograms(updatedPrograms)
        setClients(updatedClients)
      } else {
        const response = await axios.get('/clients/getClientNames')
        const {data} = response?.data
        const updatedClients = data.map((client: any) => ({
          value: client._id,
          label: client.fullName,
        }))
        setClients(updatedClients)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
      toast.error('Failed to fetch clients')
    } finally {
      setClientsLoading(false)
      if (isCaregiverType) {
        setProgramsLoading(false)
      }
    }
  }

  const handleClose = () => {
    setSelectedClient(null)
    setNoteType(null)
    setAssessmentError(null)
    onClose()
  }

  const checkAssessment = async () => {
    if (
      !selectedClient ||
      (isWoundType ? !date : !noteType) ||
      (isCaregiverType && !selectedProgram)
    ) {
      toast.error('Please select both client and note type')
      return
    }

    setLoading(true)
    setAssessmentError(null)

    try {
      let response
      if (isWoundType) {
        response = await axios.post(noteCheckingRoute, {
          clientId: selectedClient.value,
          date: date,
        })
      } else if (isCaregiverType) {
        response = await axios.post(noteCheckingRoute, {
          clientId: selectedClient.value,
          programId: selectedProgram.value,
          noteType: noteType.value,
        })
      } else {
        response = await axios.post(noteCheckingRoute, {
          clientId: selectedClient.value,
          noteType: noteType.value,
        })
      }
      const {exists} = response.data

      if (exists) {
        // Assessment exists, navigate to entry form
        if (isWoundType) {
          redirectToNewPage({
            clientId: selectedClient.value,
            data: response.data,
          })
        } else if (isCaregiverType) {
          redirectToNewPage({
            clientId: selectedClient.value,
            programId: selectedProgram.value,
            noteType: noteType.value,
            data: response.data,
          })
        } else {
          redirectToNewPage({
            clientId: selectedClient.value,
            noteType: noteType.value,
            data: response.data,
          })
        }
        onClose()
      } else {
        // No assessment found
        if (isWoundType) {
          setAssessmentError(
            `The client ${selectedClient.label} does not have an Assessment for the selected date. They cannot create an entry now.`
          )
        } else if (isCaregiverType) {
          setAssessmentError(
            `The client ${selectedClient.label} does not have an Assessment in the ${noteType.label} category for the selected program. They cannot create an entry now.`
          )
        } else {
          setAssessmentError(
            `The client ${selectedClient.label} does not have an Assessment in the ${noteType.label} category. They cannot create an entry now.`
          )
        }
      }
    } catch (error) {
      console.error('Error checking assessment:', error)
      toast.error('Failed to check assessment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        },
        '& .MuiDialog-paper': {
          overflow: 'visible', // This makes the dialog content overflow visible
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          fontWeight: 600,
          fontSize: '1.25rem',
          padding: '20px 24px',
        }}
      >
        Create Note Entry Report
      </DialogTitle>

      <DialogContent sx={{padding: '24px', overflow: 'visible'}}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
          <Typography variant='body1' color='text.secondary' sx={{mt: 1}}>
            Please select the criteria for generating the note entry report.
          </Typography>

          {assessmentError && (
            <Alert severity='warning' sx={{mb: 2}}>
              {assessmentError}
            </Alert>
          )}
          {isCaregiverType && (
            <Box>
              <SearchableSelect
                label='Select Program'
                options={programs}
                value={selectedProgram?.value || ''}
                onChange={(newValue) => setSelectedProgram(newValue)}
                fullWidth
                size='small'
                disabled={loading || programsLoading}
                endAdornment={programsLoading ? <CircularProgress size={20} /> : null}
              />
            </Box>
          )}
          <Box>
            <SearchableSelect
              label='Select Client'
              options={clients}
              value={selectedClient?.value || ''}
              onChange={(newValue) => setSelectedClient(newValue)}
              fullWidth
              size='small'
              disabled={loading || clientsLoading}
              endAdornment={clientsLoading ? <CircularProgress size={20} /> : null}
            />
          </Box>
          {/* Date Range */}
          <Box>
            {isWoundType ? (
              <Box sx={{display: 'flex', gap: 2}}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='Select Date'
                    sx={{background: 'white', mt: 1}}
                    value={date}
                    onChange={handleStartDateChange}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                      textField: {
                        margin: 'normal',
                        fullWidth: true,
                        size: 'small',
                        disabled: false,
                        inputProps: {
                          style: {cursor: 'pointer'},
                          readOnly: true,
                        },
                        onClick: (e) => {
                          // Prevent the click from bubbling up and trigger the date picker
                          e.preventDefault()
                          e.stopPropagation()
                          // Find and click the calendar icon to open the picker
                          const iconButton =
                            e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') ||
                            e.currentTarget.querySelector('.MuiIconButton-root')
                          if (iconButton) {
                            ;(iconButton as HTMLElement).click()
                          }
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            ) : (
              <Box sx={{display: 'flex', gap: 2}}>
                <Autocomplete
                  options={[
                    {label: 'DAILY', value: 'DAILY'},
                    {label: 'WEEKLY', value: 'WEEKLY'},
                    {label: 'MONTHLY', value: 'MONTHLY'},
                    {label: 'QUARTERLY', value: 'QUARTERLY'},
                    {label: 'YEARLY', value: 'YEARLY'},
                  ]}
                  renderInput={(params) => <TextField {...params} label='Note Type' />}
                  value={noteType}
                  onChange={(event, newValue) => setNoteType(newValue)}
                  fullWidth
                  size='small'
                  disabled={loading || clientsLoading}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px',
          gap: '12px',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          sx={{
            borderColor: '#6c757d',
            color: '#6c757d',
            '&:hover': {
              borderColor: '#495057',
              backgroundColor: '#f8f9fa',
            },
            fontWeight: 500,
            padding: '8px 20px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={checkAssessment}
          variant='contained'
          disabled={
            loading || clientsLoading || !selectedClient || (isWoundType ? !date : !noteType)
          }
          sx={{
            backgroundColor: '#007bff',
            '&:hover': {
              backgroundColor: '#0056b3',
            },
            fontWeight: 500,
            padding: '8px 20px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          }}
        >
          {loading ? <CircularProgress size={20} color='inherit' /> : 'Check Assessment & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NoteEntryModal
